// Scaffolding derived from registry content for the sign-off review view —
// mirrors docs/validation/render_registry_review.py so the console and the baked
// review doc surface the same things.

export type ReviewItemKind = 'PROVISIONAL' | 'MA_REQUIRED' | 'MA note';
export type ReviewItem = { where: string; kind: ReviewItemKind; detail: string };

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Every place in the content explicitly needing Medical-Advisory attention:
 * `_provisional` sections, `MA_REQUIRED` disposition cells, embedded `_ma_note`s. */
export function collectReviewItems(value: unknown, path: string[] = ['registry']): ReviewItem[] {
  const items: ReviewItem[] = [];
  const here = path.join(' › ');
  if (isObj(value)) {
    if (value._provisional === true) {
      items.push({ where: here, kind: 'PROVISIONAL', detail: String(value._doc ?? 'Section marked provisional — awaiting MA sign-off.') });
    }
    if ('_ma_note' in value) {
      items.push({ where: here, kind: 'MA note', detail: String(value._ma_note) });
    }
    const outcome = value.outcome;
    if (isObj(outcome) && outcome.stance === 'MA_REQUIRED') {
      const rid = value.rule_id ? ` [${String(value.rule_id)}]` : '';
      items.push({ where: here + rid, kind: 'MA_REQUIRED', detail: 'Disposition cell needs the MA to sign the stance value; determinate:false until signed.' });
    }
    for (const [k, v] of Object.entries(value)) {
      if (!k.startsWith('_')) items.push(...collectReviewItems(v, [...path, k]));
    }
  } else if (Array.isArray(value)) {
    value.forEach((x, i) => { items.push(...collectReviewItems(x, [...path, `[${i}]`])); });
  }
  return items;
}

/** First sentence of a doc string, for the framing bullets. */
export function firstSentence(s: unknown): string {
  const t = String(s ?? '').trim();
  const m = t.match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : t).trim();
}

/** The `_doc` note on a body section, if any. */
export function sectionDoc(section: unknown): string {
  return isObj(section) ? String(section._doc ?? '') : '';
}

const META_KEYS = ['version', 'last_updated', 'status', 'notes'];

export type RegistryMeta = { version?: unknown; last_updated?: unknown; status?: unknown; notes?: unknown };

/** Pull the top-level meta (version/status/notes) out of the content so it can
 * render as a banner rather than raw content rows; return the rest as the body. */
export function splitRegistryMeta(content: unknown): { meta: RegistryMeta; body: unknown } {
  if (!isObj(content)) return { meta: {}, body: content };
  const meta: RegistryMeta = {};
  const body: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(content)) {
    if (META_KEYS.includes(k)) (meta as Record<string, unknown>)[k] = v;
    else body[k] = v;
  }
  return { meta, body };
}
